// SPDX-License-Identifier: BUSL-1.1

/**
 * Organization switcher dropdown component.
 *
 * Allows users to switch between organizations they have access to.
 * Shows current org name with role badge and a dropdown of available orgs.
 * Super admins and admins can create new organizations.
 *
 * PERPLEXITY VALIDATION EDGE CASES:
 * - Role field naming: use 'membershipRole' to clarify it's user-relative, not org property
 * - RLS + org listing: GET /api/orgs join must not bypass RLS via unscoped subqueries
 * - "Create org" only shown to admins/super_admins
 * - Switching org triggers full re-render of OrgContext
 */

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ChevronsUpDown, Building2, PlusIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { showSuccess } from '@/lib/toast';
import { apiFetch, shouldRetryApiQuery } from '@/lib/api-client';
import { ORGS_QUERY_KEY, queryKeyLabel } from '@/lib/bootstrap-query-keys';
import { useOrg } from '@/contexts/org-context';
import { createOrganization } from '@/lib/orgs';

/** Role badge colour map — same as keys.tsx */
function RoleBadge({ role }: { role: string }) {
  const map: Record<string, string> = {
    owner: 'bg-amber-900/50 text-amber-300',
    admin: 'bg-blue-900/50 text-blue-300',
    member: 'bg-neutral-800 text-neutral-300',
    viewer: 'bg-neutral-800 text-neutral-400',
  };
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${map[role] ?? 'bg-neutral-800 text-neutral-400'}`}>
      {role}
    </span>
  );
}

interface OrgWithRole {
  id: string;
  name: string;
  slug: string;
  status: string;
  membershipRole?: string | null; // PERPLEXITY: user-relative role, not org property
}

async function fetchOrgs(): Promise<OrgWithRole[]> {
  const data = await apiFetch<{ data?: OrgWithRole[] } | OrgWithRole[]>('/orgs', {
    _queryKey: queryKeyLabel(ORGS_QUERY_KEY),
  });
  return Array.isArray(data) ? data : (data.data ?? []);
}

async function createOrg(name: string, slug?: string): Promise<OrgWithRole> {
  return createOrganization({
    name,
    slug: slug ?? '',
  });
}

/** Returns true if current user's membershipRole in any org grants admin-level access */
function canManageOrgs(orgs: OrgWithRole[]): boolean {
  return orgs.some((o) => o.membershipRole === 'owner' || o.membershipRole === 'admin');
}

/**
 * Organization switcher dropdown.
 *
 * Renders in the app header and allows quick org switching.
 * Hidden when only one org is available and loading is done.
 * Super admins/admins see a "Create organization" item.
 */
export function OrgSwitcher() {
  const { organizations, currentOrg, switchOrg, isLoading, bootstrapReady, needsOnboarding } = useOrg();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSlug, setNewOrgSlug] = useState('');
  const [createError, setCreateError] = useState('');
  const [createPending, setCreatePending] = useState(false);

  // Fetch enriched org list (includes membershipRole from ops_org_memberships join)
  const { data: enrichedOrgs = organizations } = useQuery<OrgWithRole[]>({
    queryKey: ORGS_QUERY_KEY,
    queryFn: () => fetchOrgs(),
    enabled: bootstrapReady,
    staleTime: 30_000,
    retry: shouldRetryApiQuery,
  });

  // Auto-generate slug from name
  function handleNameChange(name: string) {
    setNewOrgName(name);
    setNewOrgSlug(
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48),
    );
  }

  const queryClient = useQueryClient();

  async function handleCreateOrg() {
    if (!newOrgName.trim()) {
      setCreateError('Organization name is required');
      return;
    }
    setCreatePending(true);
    setCreateError('');
    try {
      const newOrg = await createOrg(newOrgName.trim(), newOrgSlug || undefined);
      showSuccess(`Organization "${newOrg.name}" created`);
      setShowCreateDialog(false);
      setNewOrgName('');
      setNewOrgSlug('');

      // Invalidate React Query cache so org list refetches with the new org
      await queryClient.invalidateQueries({ queryKey: ORGS_QUERY_KEY });

      // Switch to the newly created org
      switchOrg(newOrg.id);
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setCreatePending(false);
    }
  }

  if (isLoading) return null;

  const orgs: OrgWithRole[] = enrichedOrgs.length > 0 ? enrichedOrgs : (organizations as OrgWithRole[]);
  const showCreate = needsOnboarding || canManageOrgs(orgs);

  // Hide when only one org (unless loading or onboarding needs a create CTA)
  if (orgs.length <= 1 && !showCreate) return null;

  // Resolve current role for the active org
  const currentRole = orgs.find((o) => o.id === currentOrg?.id)?.membershipRole;

  return (
    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              className="flex items-center gap-2 text-sm text-neutral-300 hover:bg-neutral-800/50"
            >
              <Building2 className="size-4 text-emerald-400" />
              <span className="font-medium">{currentOrg?.name ?? (needsOnboarding ? 'Create workspace' : 'Select Org')}</span>
              {currentRole && <RoleBadge role={currentRole} />}
              <ChevronsUpDown className="size-3 text-neutral-500" />
            </Button>
          }
        />
        <DropdownMenuContent align="end" side="bottom" className="w-64">
          <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
            {needsOnboarding ? 'Workspace setup' : 'Organizations'}
          </div>
          {orgs.map((org) => (
            <DropdownMenuItem
              key={org.id}
              onClick={() => switchOrg(org.id)}
              className={org.id === currentOrg?.id ? 'bg-neutral-800' : ''}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex flex-col">
                  <span className="font-medium">{org.name}</span>
                  <span className="text-xs text-neutral-500">@{org.slug}</span>
                </div>
                <RoleBadge role={org.membershipRole ?? ''} />
              </div>
            </DropdownMenuItem>
          ))}
          {showCreate && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setShowCreateDialog(true);
                }}
                className="text-emerald-400 focus:text-emerald-300"
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                {needsOnboarding ? 'Create workspace' : 'Create organization'}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create organization dialog */}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            Create a new organization to separate teams, billing, and resources.
            You will become the owner.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-neutral-300" htmlFor="org-name">
              Name
            </label>
            <Input
              id="org-name"
              placeholder="e.g. Acme Corp"
              value={newOrgName}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-neutral-300" htmlFor="org-slug">
              URL slug
            </label>
            <Input
              id="org-slug"
              placeholder="acme-corp"
              value={newOrgSlug}
              onChange={(e) => setNewOrgSlug(e.target.value)}
            />
            <p className="text-xs text-neutral-500">
              Auto-generated from name. Letters, numbers, and hyphens only.
            </p>
          </div>
          {createError && <p className="text-sm text-red-400">{createError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
            Cancel
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-500"
            onClick={handleCreateOrg}
            disabled={createPending}
          >
            {createPending ? 'Creating...' : 'Create organization'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
